import React, { FunctionComponent, PropsWithChildren, useEffect } from 'react';
import {
  RecoilState,
  RecoilValueReadOnly,
  useRecoilValue,
  useSetRecoilState,
} from 'recoil';

import { vi } from 'vitest';

import { Maybe } from '../types';

export type MockedRecoilStateProps<T> = {
  atoms: Array<MockedRecoilAtom<T>>;
};

export type MockedRecoilAtom<T> = {
  atom: RecoilState<T>;
  defaultValue: T;
};

export type MockedRecoilStateFunctionComponent<T = any> = FunctionComponent<
  PropsWithChildren<MockedRecoilStateProps<T>>
>;

export const MockedRecoilState: MockedRecoilStateFunctionComponent = (
  props,
) => {
  const setters: Array<CallableFunction> = [];
  const defaultValues: any[] = [];

  props.atoms.forEach((mockedAtom: MockedRecoilAtom<any>) => {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const setter = useSetRecoilState<any>(mockedAtom.atom);
    setters.push(setter);
    defaultValues.push(mockedAtom.defaultValue);
  });

  useEffect(() => {
    setters.map((setter, index: number) => setter(defaultValues[index]));
  }, []);

  return <>{props.children}</>;
};

export const checkIfAllExist = (
  selectorsArray: string[],
  wrapper: HTMLElement,
): void =>
  selectorsArray.forEach((el) => {
    if (!wrapper.getElementsByClassName(el).length) {
      throw new Error(`Element ${el} was not rendered`);
    }
  });

export function GetAtomValue<T>({
  atom,
}: {
  atom: RecoilState<T> | RecoilValueReadOnly<T>;
}): JSX.Element {
  return <>{JSON.stringify(useRecoilValue(atom))}</>;
}

export const getEnumRandomValue = (enumSource: Record<string, any>) => {
  const rand = Math.floor(Math.random() * Object.keys(enumSource).length);
  return enumSource[Object.keys(enumSource)[rand]];
};

export const randomString = () => (Math.random() + 1).toString(36).substring(7);

export const RecoilObserver = ({
  atomOrSelector,
  onChange,
}: {
  atomOrSelector: RecoilState<any> | RecoilValueReadOnly<any>;
  onChange: (v: any) => void;
}): null => {
  const value = useRecoilValue(atomOrSelector);
  useEffect(() => onChange(value), [onChange, value]);
  return null;
};

export const makeTextExcerpt = (
  text: Maybe<string>,
  length: number,
  suffix: string = '…',
) => (text && text.length > length ? text.substring(0, length) + suffix : text);

export const randomNaturalNumber = (min = 1, max = 100) =>
  Math.floor(Math.random() * (max - min + 1)) + min;

export const geti18nMock = () => ({
  useTranslation: () => ({
    t: (key: string) => key,
    i18n: {
      changeLanguage: () => new Promise(() => {}),
    },
  }),
});
